import React from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { BudgetValidationQueueRow } from './types';
import { decisionColor, formatMoney } from './format';

type Props = {
  selected: BudgetValidationQueueRow | null;
  onDecide: (decisionCode: string, amount: number, reason: string) => Promise<void>;
};

type State = {
  amount: number;
  reason: string;
  saving: boolean;
};

class DecisionPanel extends React.Component<Props, State> {
  state: State = {
    amount: 0,
    reason: '',
    saving: false,
  };

  componentDidUpdate(prevProps: Props) {
    if (prevProps.selected?.rid !== this.props.selected?.rid && this.props.selected) {
      const row = this.props.selected;
      this.setState({
        amount: Number(row.rca || row.rqa || 0),
        reason: row.dcr || '',
      });
    }
  }

  submit = async (decisionCode: string) => {
    const { selected } = this.props;
    const { amount, reason } = this.state;

    if (!selected) return;

    this.setState({ saving: true });

    try {
      await this.props.onDecide(decisionCode, Number(amount || 0), reason);
    } finally {
      this.setState({ saving: false });
    }
  };

  render() {
    const { selected } = this.props;
    const { amount, reason, saving } = this.state;

    if (!selected) {
      return (
        <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6">Validation Decision</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Select an item from the validation queue.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Validation Decision</Typography>
            <Chip label={selected.dcc || 'PENDING'} color={decisionColor(selected.dcc)} />
          </Stack>

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {selected.nam}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selected.dsc}
            </Typography>
          </Box>

          <Divider />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Source</Typography>
              <Typography variant="body2">{selected.src}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Cost Center</Typography>
              <Typography variant="body2">{selected.ccc} {selected.ccn}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Account</Typography>
              <Typography variant="body2">{selected.acc} {selected.acn}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Score</Typography>
              <Typography variant="body2">{Number(selected.scr || 0).toFixed(2)}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Requested</Typography>
              <Typography variant="body2">{formatMoney(selected.rqa)}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Recommended</Typography>
              <Typography variant="body2">{formatMoney(selected.rca)}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Priority</Typography>
              <Typography variant="body2">{selected.prc}</Typography>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Typography variant="caption" color="text.secondary">Mandatory</Typography>
              <Typography variant="body2">{selected.mdf ? 'Yes' : 'No'}</Typography>
            </Grid>
          </Grid>

          <Divider />

          <TextField
            label="Decision Amount"
            type="number"
            value={amount}
            onChange={(e) => this.setState({ amount: Number(e.target.value || 0) })}
            fullWidth
          />

          <TextField
            label="Decision Reason"
            value={reason}
            onChange={(e) => this.setState({ reason: e.target.value })}
            multiline
            minRows={4}
            fullWidth
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button disabled={saving} variant="contained" color="success" onClick={() => this.submit('APPROVED')}>
              Approve
            </Button>
            <Button disabled={saving} variant="contained" color="warning" onClick={() => this.submit('REDUCED')}>
              Reduce
            </Button>
            <Button disabled={saving} variant="outlined" color="error" onClick={() => this.submit('REJECTED')}>
              Reject
            </Button>
            <Button disabled={saving} variant="outlined" onClick={() => this.submit('HOLD')}>
              Hold
            </Button>
            <Button disabled={saving} variant="outlined" color="info" onClick={() => this.submit('ESCALATED')}>
              Escalate
            </Button>
          </Stack>
        </Stack>
      </Paper>
    );
  }
}

export default DecisionPanel;
